import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { AuthCallback } from '@/components/AuthCallback';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { ThemeProvider } from '@/components/ThemeProvider';
import AdminLayout from '@/components/AdminLayout';
import ScrollToTop from '@/components/ScrollToTop';
import KeepAlive from '@/components/KeepAlive';
import { useState, useEffect } from 'react';
import { getCart } from '@/lib/api';
import '@/App.css';

// Lazy load pages
import Home from '@/pages/Home';
import Shop from '@/pages/Shop';
import Collections from '@/pages/Collections';
import ProductDetail from '@/pages/ProductDetail';
import Cart from '@/pages/Cart';
import Checkout from '@/pages/Checkout';
import Login from '@/pages/Login';
import Account from '@/pages/Account';
import Orders from '@/pages/Orders';
import OrderDetail from '@/pages/OrderDetail';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/BlogPost';
import About from '@/pages/About';
import Contact from '@/pages/Contact';
import FAQ from '@/pages/FAQ';
import ShippingReturns from '@/pages/ShippingReturns';
import AdminDashboard from '@/pages/admin/Dashboard';
import AdminProducts from '@/pages/admin/Products';
import AdminOrders from '@/pages/admin/Orders';
import AdminSiteSettings from '@/pages/admin/SiteSettings';
import AdminSiteBuilder from '@/pages/admin/SiteBuilder';
import AdminCustomers from '@/pages/admin/Customers';
import AdminCustomerDetail from '@/pages/admin/CustomerDetail';
import AdminReturns from '@/pages/admin/Returns';
import AdminDiscounts from '@/pages/admin/Discounts';
import AdminReviews from '@/pages/admin/Reviews';
import AdminAnalytics from '@/pages/admin/Analytics';
import AdminLoyalty from '@/pages/admin/Loyalty';
import AdminSecurity from '@/pages/admin/Security';
import AdminNotifications from '@/pages/admin/Notifications';
import AdminLogin from '@/pages/admin/AdminLogin';
import CheckoutSuccess from '@/pages/CheckoutSuccess';
import VerifyEmailPending from '@/pages/VerifyEmailPending';
import VerifyEmail from '@/pages/VerifyEmail';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';

function AppRouter() {
  const location = useLocation();
  
  // Detect session_id during render (before useEffect)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/shop" element={<Shop />} />
      <Route path="/product/:id" element={<ProductDetail />} />
      <Route path="/cart" element={<Cart />} />
      <Route path="/login" element={<Login />} />
      <Route path="/verify-email-pending" element={<VerifyEmailPending />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:id" element={<BlogPost />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/collections" element={<Collections />} />
      
      {/* Protected Routes */}
      <Route
        path="/checkout"
        element={
          <ProtectedRoute>
            <Checkout />
          </ProtectedRoute>
        }
      />
      <Route
        path="/checkout/success"
        element={
          <ProtectedRoute>
            <CheckoutSuccess />
          </ProtectedRoute>
        }
      />
      <Route
        path="/account"
        element={
          <ProtectedRoute>
            <Account />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <Orders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:id"
        element={
          <ProtectedRoute>
            <OrderDetail />
          </ProtectedRoute>
        }
      />
      
      {/* FAQ and Shipping & Returns */}
      <Route path="/faq" element={<FAQ />} />
      <Route path="/shipping" element={<ShippingReturns />} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/products"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminProducts />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/orders"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminOrders />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/settings"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminSiteSettings />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customers"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminCustomers />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/customers/:customerId"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminCustomerDetail />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/returns"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminReturns />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/discounts"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminDiscounts />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/reviews"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminReviews />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/site-builder"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminSiteBuilder />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/analytics"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminAnalytics />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/loyalty"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminLoyalty />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/security"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminSecurity />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/notifications"
        element={
          <ProtectedRoute adminOnly>
            <AdminLayout>
              <AdminNotifications />
            </AdminLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function AppContent() {
  const [cartCount, setCartCount] = useState(0);
  const location = useLocation();

  useEffect(() => {
    // Update cart count
    getCart()
      .then(res => {
        const count = res.data.reduce((sum, item) => sum + item.quantity, 0);
        setCartCount(count);
      })
      .catch(() => setCartCount(0));
  }, [location]);

  // Hide nav/footer on auth callback
  const hideLayout = location.hash?.includes('session_id=');

  return (
    <>
      {!hideLayout && <Navbar cartCount={cartCount} />}
      <main className={hideLayout ? '' : 'min-h-screen'}>
        <AppRouter />
      </main>
      {!hideLayout && <Footer />}
      <Toaster position="top-center" richColors />
    </>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ThemeProvider>
          <KeepAlive />
          <ScrollToTop />
          <AppContent />
        </ThemeProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}